/**
 * THE BLOCKERS BANNER AND THE CHECKLIST — one pane, because they are one question.
 *
 * ★★ EVERY NUMBER ON THIS PANE COMES OFF `getStructuralRules`, AND THE POLICY VERSION IS PRINTED.
 * That operation exists so *"the editor's client-side mirror renders these, so client and server
 * cannot drift"* (authors-api.yaml:5144), and it names the version the submit check will pin onto
 * the sealed article version, *"so a refusal can be reproduced later"*. The prototype's hardcoded
 * **600 words** is not a number this platform has ever held — the ruled band is **300 to 7,000**
 * (authors-api.yaml:1406-1407) — and the fix is not to write 300 here instead: it is to render what
 * the server sent, which is what ../document.ts does and why no bound appears in this file.
 *
 * ★★ SEVEN ROWS ARE DRAWN IN THE PROTOTYPE AND EIGHT ARE RULED, AND THEY ARE NOT THE SAME SEVEN.
 * The one that must never appear is the prototype's item 7, "no medical or legal claims": it is a
 * moderation judgement, and *"No number from the moderation pipeline appears here"*
 * (authors-api.yaml:1817, restated on the review record at :4907). It is not rendered here, not
 * approximated, and not softened into a hint.
 *
 * ★ THE 422 IS THE WHOLE FAILURE SET. `submitArticle` returns every failed rule at once *"so an
 * author fixes everything in one pass"* (authors-api.d.ts:1443-1444), never the first alone — so
 * the refusal below is a list and the screen never shows one failure at a time.
 *
 * ★ AND ONE ROW IS NOT D21'S: "Web address category" (design 14, F8). The submit refuses a missing
 * pick by its own code, `ADDRESS_CATEGORY_REQUIRED`, BEFORE it asks the structural rules - so a
 * pane that listed only the eight said "nothing to fix" over a Send that was bound to fail (found
 * on the cabinet walk of 2026-10-01). The editor builds that row itself (./address.ts) and hands it
 * in after the eight; it carries no number, so nothing above about the policy's numbers is bent.
 * This pane draws every row it is given the same way, and the badge counts them the same way.
 *
 * ★ DESIGN 15 (the owner's live look at real cabinets, 2026-10-01: "no internal or technical words
 * in front of an author"). Three things above are no longer what the pane DRAWS, though every
 * number still comes off `getStructuralRules` exactly as argued:
 *   - THE POLICY VERSION IS NOT PRINTED. It is a code - the fixture's "structural-limits/2026-07-01",
 *     a 64-character digest live (src/modules/articles/reference/editor-reference.presenter.ts);
 *     `policyVersion` is still taken, so a caller hands over what it read, and nothing prints it.
 *   - THE WORD-COUNT DEFINITION IS SAID IN PLAIN WORDS (`howWordsAreCounted`), not as the
 *     platform's own sentence ("NFC-normalised", "the legacy editor's number"); `wordCountDefinition`
 *     is taken and not printed, as above. The plain sentence restates that definition, nothing more.
 *   - THE REFUSAL NAMES EACH RULE BY ITS CHECKLIST LABEL, never its code (`word_count`), and it is
 *     drawn by `SubmitRefusal` below - which the editor puts at the top of the page beside its other
 *     refusals, so a Send pressed there is answered there (../EditorRoute.tsx). This pane still draws
 *     it when a caller hands it `refusal`.
 * And the badges: "Ready", "Not yet" and "Cannot check" are `--mx-text` words on their state's tint,
 * with the state's colour on the mark before the label. The coloured words read 3.99 (danger) and
 * 4.34 (success) on the card surface in the light scheme - the e2e scan found them once the cabinet's
 * cards moved onto `--mx-surface` - and the text ink reads on any tint in both schemes
 * (../../../../test/routes/articles/editor-inks.spec.tsx measures them on that surface).
 *
 * ★ AN APPLICANT WRITES, AND SENDING WAITS FOR APPROVAL (design 25, section 6 and P14). For a
 * session that may write drafts but not use the editor's Submit (`writesDraftsBeforeApproval`,
 * features/auth/session/session-state.ts), the editor puts `ApprovalFirst` where Submit would be -
 * "Your author application is approved first", a link to the author profile - and this pane says
 * why under its title, in `AUTHOR_APPROVAL_REQUIRED`'s own words (features/cabinet/api/problem.ts).
 * The rows stay: they are what will be checked once the application is approved.
 */
import { Alert, Badge, Button, List, Stack, Text, Title } from '@mantine/core';
import type { MouseEvent } from 'react';
import { CircleAlert, CircleCheck, CircleHelp } from '@moonx/ui';
import type { LucideIcon } from '@moonx/ui';
import type { ChecklistRow } from './document';
import type { StructuralLimitFailure } from '../../../features/cabinet/api/editor-api';
import { InAppAnchor } from '../../../features/shell/InAppAnchor';
import { followsInApp, useNavigateToHref } from '../../../features/shell/in-app-navigation';
import { Trans, useTranslation } from '../../../i18n';
import { APPLY_ROUTE } from '../../apply';
import type { ArticlesKey, ArticlesT } from '../articles-i18n';
import classes from './editor.module.css';
import { usePieceContext } from './piece-context';

export interface SubmitChecklistProps {
  readonly rows: readonly ChecklistRow[];
  /**
   * True on a piece the author cannot change (published, with an editor, scheduled, taken down,
   * archived): nothing is checked before sending, so no row is drawn - the owner, 2026-10-05, after
   * old imported articles showed red rows for today's rules they can never be sent against.
   */
  readonly readOnly?: boolean;
  /** The version `getStructuralRules` named. Taken and not printed: it is a code (design 15). */
  readonly policyVersion: string;
  /** The platform's own sentence for the word count. Taken and not printed (design 15). */
  readonly wordCountDefinition: string;
  /**
   * The last 422's failure set, if the author has pressed Submit and been refused. The editor draws
   * it at the top of the page instead (`SubmitRefusal`) and passes none here.
   */
  readonly refusal: readonly StructuralLimitFailure[] | undefined;
  /** True for an applicant: sending waits for the author application's approval (the header). */
  readonly awaitingApproval?: boolean;
}

/** The badge's words and its tint. The words are always `--mx-text` - see the header. */
const STATE_BADGE: Readonly<Record<ChecklistRow['state'], { text: ArticlesKey; token: string }>> = {
  ok: { text: 'editor.checklist.badge.ok', token: '--mx-success' },
  failing: { text: 'editor.checklist.badge.failing', token: '--mx-danger' },
  // NOT A TICK. See ../document.ts: a rule the policy sent no bounds for has told the client
  // nothing, and a green row for "we could not check" is the failure this screen exists to avoid.
  unknowable: { text: 'editor.checklist.badge.unknowable', token: '--mx-warning' },
};

/** The mark before a row's label: its state's colour, beside words that say the same thing. */
const STATE_MARK: Readonly<Record<ChecklistRow['state'], { icon: LucideIcon; className: string }>> =
  {
    ok: { icon: CircleCheck, className: classes.stateOk ?? '' },
    failing: { icon: CircleAlert, className: classes.stateFailing ?? '' },
    unknowable: { icon: CircleHelp, className: classes.stateUnknowable ?? '' },
  };

/*
 * How the word count counts, in plain words (`editor.checklist.howWordsAreCounted`). It restates the
 * platform's definition (the server's `WORD_COUNT_DEFINITION`,
 * src/modules/articles/reference/editor-reference.presenter.ts) - the one the checklist's own count
 * runs through (features/cabinet/api/editor-measure.ts) - without its technical terms.
 */

export function SubmitChecklist({
  rows,
  refusal,
  readOnly = false,
  awaitingApproval = false,
}: SubmitChecklistProps) {
  const { t } = useTranslation('articles');
  const context = usePieceContext();
  if (readOnly) {
    return <QuietChecklist />;
  }
  return (
    <Stack component="section" gap="sm" aria-labelledby="editor-checklist-title">
      <Stack gap={4}>
        <Title id="editor-checklist-title" order={2} size="h5">
          {t('editor.checklist.title')}
        </Title>
        <Text className={classes.paneNote}>{t('editor.checklist.intro', { context })}</Text>
      </Stack>

      {awaitingApproval ? <ApprovalFirstNote /> : null}

      {refusal === undefined ? null : <SubmitRefusal failures={refusal} rows={rows} />}

      <Stack gap="xs" component="ul" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {rows.map((row) => {
          const badge = STATE_BADGE[row.state];
          const mark = STATE_MARK[row.state];
          const Mark = mark.icon;
          return (
            <Stack
              key={row.rule}
              component="li"
              gap={2}
              style={{
                borderBottom: '1px solid oklch(var(--mx-border))',
                paddingBottom: 'var(--mantine-spacing-xs)',
              }}
            >
              <Text size="sm" fw={700}>
                <Mark
                  size={14}
                  className={`${classes.stateIcon ?? ''} ${mark.className}`}
                  aria-hidden
                />
                {row.label}{' '}
                {/* A `span`: the label is a `<p>`, which cannot hold Mantine's default `<div>`. */}
                <Badge
                  component="span"
                  size="xs"
                  radius="sm"
                  styles={{
                    root: {
                      backgroundColor: `oklch(var(${badge.token}) / 0.14)`,
                      color: 'oklch(var(--mx-text))',
                    },
                  }}
                >
                  {t(badge.text)}
                </Badge>
              </Text>
              <Text size="xs" c="dimmed">
                {row.detail}
              </Text>
            </Stack>
          );
        })}
      </Stack>

      <Text size="xs" c="dimmed">
        {t('editor.checklist.howWordsAreCounted')}
      </Text>
    </Stack>
  );
}

export interface SubmitRefusalProps {
  /** The 422's whole failure set. */
  readonly failures: readonly StructuralLimitFailure[];
  /** The checklist's rows, whose labels name each failed rule. */
  readonly rows: readonly ChecklistRow[];
}

/** A failed rule's name for the author: its checklist label, never the rule's code. */
function failureName(
  failure: StructuralLimitFailure,
  rows: readonly ChecklistRow[],
  t: ArticlesT,
): string {
  return rows.find((row) => row.rule === failure.rule)?.label ?? t('editor.checklist.anotherRule');
}

/**
 * THE 422, AS ONE LIST (the header: the whole failure set, never the first failure alone). Every
 * rule is named by its checklist label, with the bound and what the piece has, so the author fixes
 * everything in one pass.
 */
export function SubmitRefusal({ failures, rows }: SubmitRefusalProps) {
  const { t } = useTranslation('articles');
  const context = usePieceContext();
  return (
    <Alert
      variant="light"
      role="alert"
      title={t('editor.checklist.refusalTitle', { context })}
      styles={{
        root: {
          backgroundColor: 'oklch(var(--mx-danger) / 0.10)',
          border: '1px solid oklch(var(--mx-danger) / 0.45)',
        },
        title: { color: 'oklch(var(--mx-danger))' },
      }}
    >
      <Stack gap={4}>
        <Text size="sm">{t('editor.checklist.refusalIntro')}</Text>
        <List size="sm" spacing={2}>
          {failures.map((failure) => (
            <List.Item key={`${failure.rule}:${String(failure.actual)}`}>
              <Trans
                t={t}
                i18nKey="editor.checklist.refusalLine"
                context={context}
                values={{
                  name: failureName(failure, rows, t),
                  limit: failure.limit,
                  actual: failure.actual,
                }}
                components={{ rule: <Text span size="sm" fw={700} /> }}
              />
            </List.Item>
          ))}
        </List>
      </Stack>
    </Alert>
  );
}

/**
 * ★ THE CHECKLIST ON A PIECE THE AUTHOR CANNOT CHANGE (the owner, 2026-10-05: "ok, yes" to keeping
 * it quiet there, as the cover row already is). An imported published article was written under the
 * old apps' rules - covers of 1040 by 800, lengths and categories nobody counted - and showed "Not
 * yet" against today's, though it will never be sent again. The rows are for a send, so where no
 * send is possible the pane says so in one line and draws none. A piece sent back with notes is the
 * author's to change, and keeps every row.
 */
function QuietChecklist() {
  const { t } = useTranslation('articles');
  const context = usePieceContext();
  return (
    <Stack component="section" gap={4} aria-labelledby="editor-checklist-title">
      <Title id="editor-checklist-title" order={2} size="h5">
        {t('editor.checklist.title')}
      </Title>
      <Text className={classes.paneNote}>{t('editor.checklist.nothingToCheck', { context })}</Text>
    </Stack>
  );
}

/* ================================================== before approval (design 25, section 6) */

/*
 * `AUTHOR_APPROVAL_REQUIRED`'s words (features/cabinet/api/problem.ts), and the button's, are the
 * catalogue's `editor.approval`.
 */

/**
 * WHERE SUBMIT WOULD BE, FOR AN APPLICANT: "Your author application is approved first", a real
 * link to the author profile (`/apply`), followed in place by a plain click - the profile is what
 * the editors approve, and where it is sent for review.
 */
export function ApprovalFirst() {
  const { t } = useTranslation('articles');
  const navigate = useNavigateToHref();
  return (
    <Button
      component="a"
      href={APPLY_ROUTE.path}
      radius="xl"
      variant="default"
      onClick={(event: MouseEvent<HTMLAnchorElement>) => {
        if (!followsInApp(event)) {
          return;
        }
        event.preventDefault();
        navigate(APPLY_ROUTE.path);
      }}
    >
      {t('editor.approval.button')}
    </Button>
  );
}

/** The same, said under the checklist's title, with the way to the profile. */
export function ApprovalFirstNote() {
  const { t } = useTranslation('articles');
  return (
    <Text size="sm">
      {t('editor.approval.sentence')}{' '}
      <InAppAnchor href={APPLY_ROUTE.path} size="sm">
        {t('editor.approval.link')}
      </InAppAnchor>
    </Text>
  );
}
